/**
 * useCarCommand — esegue i comandi toccati sul display di auto/moto (Ott 2026).
 *
 * PERCHÉ SERVE: dalla schermata in auto l'utente può chiedere "Avvia
 * registrazione" (`CarRecordScreen`). Il display non registra nulla: scrive un
 * comando che l'app sul telefono ritira qui e traduce nell'azione reale. Una
 * sola implementazione della registrazione (GPS, wake lock, notifiche) e
 * nessun doppione nativo.
 *
 * CADENZA: controllo immediato al montaggio, poi ogni 3 secondi e a ogni
 * rientro in primo piano. Così il comando viene eseguito anche se la WebView
 * era in background quando l'utente ha toccato il display (il processo è vivo
 * perché è lo stesso che ospita il servizio Android Auto).
 *
 * Il consumo è distruttivo: l'azione parte una volta sola.
 */

import { useEffect, useRef } from 'react';
import {
  addCarCommandQueuedListener,
  consumeCarCommandWithName,
  isCarBridgeAvailable,
  type CarCommandDetail,
} from '@/services/carBridge';

/**
 * Intervallo di polling dei comandi dall'auto.
 *
 * 3 secondi: è il tempo che l'utente percepisce come "immediato" quando tocca
 * "Avvia registrazione" sul display e l'app sul telefono è già viva (il
 * comando viene eseguito al controllo successivo). Il consumo è distruttivo,
 * quindi non ci sono doppie esecuzioni.
 */
export const CAR_COMMAND_POLL_MS = 3 * 1000;

/**
 * @param onCommand azione da eseguire: riceve il comando
 *   (`CAR_COMMAND_START_RECORDING`, `CAR_COMMAND_STOP_AND_SAVE`, …) e il
 *   payload opzionale — il nome percorso digitato sul display (Ott 2026)
 * @param enabled   false per disattivare (es. utente non autenticato)
 */
export function useCarCommand(
  onCommand: (command: string, name?: string | null) => void,
  enabled = true,
): void {
  // Handler sempre aggiornato senza riavviare il polling a ogni render
  const handlerRef = useRef(onCommand);
  handlerRef.current = onCommand;

  useEffect(() => {
    if (!enabled) return;
    if (!isCarBridgeAvailable()) return;

    let cancelled = false;

    const poll = async () => {
      const detail: CarCommandDetail | null = await consumeCarCommandWithName();
      if (cancelled || !detail) return;
      try {
        handlerRef.current(detail.command, detail.name);
      } catch {
        // Un handler difettoso non deve interrompere i controlli successivi
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), CAR_COMMAND_POLL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // PUSH (Milestone 1, 13 Ott 2026): il nativo avvisa appena il display
    // scrive un comando → esecuzione immediata. Il polling qui sopra resta
    // come fallback (WebView non viva, evento perso, piattaforma non nativa).
    let unsubscribe: (() => void) | null = null;
    void addCarCommandQueuedListener(() => void poll()).then((off) => {
      if (cancelled) {
        off();
        return;
      }
      unsubscribe = off;
    });

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribe?.();
    };
  }, [enabled]);
}

export default useCarCommand;
